import type { TextSettings, ThemeSettings } from '~/stores/auth'
import type { CharacterPageSettings } from '~/composables/useCharacterPage'

// --- Color utilities (exported for use by AudioPlayer, etc.) ---

export function hexToRgb(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return [r, g, b]
}

export function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(v => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('')
}

export function mixWithWhite(rgb: [number, number, number], amount: number): string {
  return rgbToHex(rgb[0] + (255 - rgb[0]) * amount, rgb[1] + (255 - rgb[1]) * amount, rgb[2] + (255 - rgb[2]) * amount)
}

export function mixWithBlack(rgb: [number, number, number], amount: number): string {
  return rgbToHex(rgb[0] * (1 - amount), rgb[1] * (1 - amount), rgb[2] * (1 - amount))
}

// --- Composable ---

interface UseLayoutStylesOptions {
  themeSettings: Ref<ThemeSettings> | ComputedRef<ThemeSettings>
  textSettings: Ref<TextSettings> | ComputedRef<TextSettings>
  settings: Ref<CharacterPageSettings> | ComputedRef<CharacterPageSettings>
  customFontUrl?: Ref<string | null> | ComputedRef<string | null>
}

export function useLayoutStyles(options: UseLayoutStylesOptions) {
  const { themeSettings, textSettings, settings } = options

  // Custom font resolution
  const fontFamily = computed(() => textSettings.value.fontFamily)
  const fontUrl = computed(() => options.customFontUrl?.value ?? settings.value.custom_font_url ?? null)
  const { resolvedFamily } = useCustomFont(fontFamily, fontUrl)

  // Theme CSS custom properties (overrides Tailwind --color-primary-*)
  const themeVars = computed(() => {
    const rgb = hexToRgb(themeSettings.value.primaryColor)
    const hex = themeSettings.value.primaryColor
    return {
      '--color-primary-50': mixWithWhite(rgb, 0.95),
      '--color-primary-100': mixWithWhite(rgb, 0.9),
      '--color-primary-200': mixWithWhite(rgb, 0.7),
      '--color-primary-300': mixWithWhite(rgb, 0.5),
      '--color-primary-400': mixWithWhite(rgb, 0.3),
      '--color-primary-500': hex,
      '--color-primary-600': mixWithBlack(rgb, 0.1),
      '--color-primary-700': mixWithBlack(rgb, 0.3),
      '--color-primary-800': mixWithBlack(rgb, 0.5),
      '--color-primary-900': mixWithBlack(rgb, 0.7),
      '--color-primary-950': mixWithBlack(rgb, 0.85)
    }
  })

  // Background gradient
  const bgGradientStyle = computed(() => {
    const rgb = hexToRgb(themeSettings.value.primaryColor)
    return {
      background: `radial-gradient(ellipse 80% 50% at 50% -20%, rgba(${rgb.join(',')}, 0.15) 0%, transparent 60%), var(--ui-bg)`
    }
  })

  // Card style
  const cardStyle = computed(() => {
    const rgb = hexToRgb(themeSettings.value.cardColor)
    const pRgb = hexToRgb(themeSettings.value.primaryColor)
    const borderStyle = themeSettings.value.cardBorderStyle ?? 'subtle'
    const borderColor = themeSettings.value.cardBorderColor || themeSettings.value.primaryColor
    const borderRgb = hexToRgb(borderColor)
    const radius = themeSettings.value.cardBorderRadius ?? 16
    const cardBg = `rgba(${rgb.join(',')}, ${themeSettings.value.cardOpacity / 100})`
    const blur = themeSettings.value.cardBlur > 0 ? `blur(${themeSettings.value.cardBlur}px)` : undefined
    const cardBackground = `radial-gradient(ellipse 100% 80% at 50% -10%, rgba(${pRgb.join(',')}, 0.08) 0%, transparent 50%), ${cardBg}`

    const style: Record<string, string | undefined> = {
      background: cardBackground,
      backdropFilter: blur,
      WebkitBackdropFilter: blur,
      boxShadow: `0 0 40px rgba(${pRgb.join(',')}, 0.06), 0 8px 32px rgba(0, 0, 0, 0.2)`,
      borderRadius: `${radius}px`
    }

    if (borderStyle === 'subtle') {
      style.border = '1px solid rgba(255, 255, 255, 0.12)'
    } else if (borderStyle === 'solid') {
      style.border = `2px solid rgba(${borderRgb.join(',')}, 0.6)`
    } else if (borderStyle === 'glow') {
      style.border = 'none'
      style.boxShadow = `0 0 8px rgba(${borderRgb.join(',')}, 0.4), 0 0 20px rgba(${borderRgb.join(',')}, 0.15), 0 8px 32px rgba(0, 0, 0, 0.2)`
    } else if (borderStyle === 'none') {
      style.border = 'none'
    }

    return style
  })

  // Avatar
  const avatarSize = computed(() => themeSettings.value.avatarSize ?? 96)
  const avatarShape = computed(() => themeSettings.value.avatarShape ?? 'circle')
  const avatarBorder = computed(() => themeSettings.value.avatarBorder ?? 'none')

  const avatarContainerStyle = computed(() => {
    const rgb = hexToRgb(themeSettings.value.primaryColor)
    const size = avatarSize.value
    const style: Record<string, string> = {
      width: `${size}px`,
      height: `${size}px`,
      overflow: 'hidden'
    }
    const blurFilter = (themeSettings.value.avatarBlur ?? 0) > 0 ? `blur(${themeSettings.value.avatarBlur}px) ` : ''
    if (avatarBorder.value === 'glow') {
      style.filter = `${blurFilter}drop-shadow(0 0 12px rgba(${rgb.join(',')}, 0.4)) drop-shadow(0 0 24px rgba(${rgb.join(',')}, 0.2))`
    } else {
      style.filter = `${blurFilter}drop-shadow(0 0 12px rgba(${rgb.join(',')}, 0.2))`
    }
    return style
  })

  const avatarClipClass = computed(() => {
    if (avatarShape.value === 'hexagon') return 'avatar-hexagon'
    if (avatarShape.value === 'rounded') return 'rounded-2xl'
    return 'rounded-full'
  })

  const avatarRingClass = computed(() => {
    if (avatarBorder.value === 'none') return ''
    if (avatarBorder.value === 'glow') return ''
    return 'ring-2 ring-primary-400/30'
  })

  const avatarImageStyle = computed(() => {
    const posX = themeSettings.value.avatarPositionX ?? 50
    const posY = themeSettings.value.avatarPositionY ?? 50
    const s = themeSettings.value.avatarScale ?? 100
    const style: Record<string, string | undefined> = {
      objectPosition: `${posX}% ${posY}%`
    }
    if (s !== 100) {
      style.transform = `scale(${s / 100})`
      style.transformOrigin = `${posX}% ${posY}%`
    }
    return style
  })

  // Text styles
  const headingStyle = computed(() => ({
    fontSize: `calc(2.5rem * ${textSettings.value.fontScale})`,
    fontWeight: textSettings.value.fontWeight,
    fontFamily: `'${resolvedFamily.value}', sans-serif`,
    color: textSettings.value.color,
    lineHeight: '1.2'
  }))

  const locationStyle = computed(() => ({
    fontSize: `calc(0.875rem * ${textSettings.value.fontScale})`,
    fontFamily: `'${resolvedFamily.value}', sans-serif`,
    color: `color-mix(in srgb, ${textSettings.value.color} 40%, transparent)`
  }))

  const bioStyle = computed(() => ({
    fontSize: `calc(1.125rem * ${textSettings.value.fontScale})`,
    fontWeight: textSettings.value.fontWeight,
    fontFamily: `'${resolvedFamily.value}', sans-serif`,
    color: `color-mix(in srgb, ${textSettings.value.color} 60%, transparent)`
  }))

  // Separator
  const separatorStyle = computed(() => {
    const style = themeSettings.value.separatorStyle ?? 'gradient'
    const pRgb = hexToRgb(themeSettings.value.primaryColor)
    if (style === 'none') return null
    if (style === 'solid') return { borderTop: '1px solid rgba(255, 255, 255, 0.1)' }
    if (style === 'dashed') return { borderTop: '1px dashed rgba(255, 255, 255, 0.1)' }
    if (style === 'glow') return { height: '1px', background: `rgba(${pRgb.join(',')}, 0.3)`, boxShadow: `0 0 8px rgba(${pRgb.join(',')}, 0.4), 0 0 16px rgba(${pRgb.join(',')}, 0.2)` }
    return { background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.06), transparent)' }
  })

  // Section ordering
  const orderedSections = computed(() => {
    const allKnown = ['links', 'games', 'clips', 'friends', 'discord', 'youtube', 'setup']
    const sections = settings.value.sections
    const order = sections?.order ?? allKnown
    // Append any new sections not yet in the saved order (migration safety)
    const missing = allKnown.filter(s => !order.includes(s))
    const fullOrder = [...order, ...missing]
    const visibility = { links: true, games: true, clips: true, friends: true, discord: true, youtube: true, setup: true, ...(sections?.visibility ?? {}) }
    return fullOrder.filter(s => visibility[s] !== false)
  })

  // Card entrance animation
  // pageEntered is provided by CharacterPageShell - false while the audio overlay is showing
  // Falls back to true when no provider exists (e.g. customize/edit mode)
  const pageEntered = inject<Ref<boolean>>('pageEntered', ref(true))
  const cardAnimation = computed(() => themeSettings.value.cardAnimation ?? 'none')

  function getCardAnimationStyle(index = 0): Record<string, string> {
    const anim = cardAnimation.value
    if (anim === 'none') return {}
    if (!pageEntered.value) return { opacity: '0' }
    const delay = index * 0.1
    return {
      animation: `card-${anim} 0.5s ease-out ${delay}s both`
    }
  }

  // Branding
  const brandRgb = computed(() => hexToRgb(themeSettings.value.primaryColor))

  return {
    themeVars,
    bgGradientStyle,
    cardStyle,
    avatarSize,
    avatarShape,
    avatarBorder,
    avatarContainerStyle,
    avatarClipClass,
    avatarRingClass,
    avatarImageStyle,
    headingStyle,
    locationStyle,
    bioStyle,
    separatorStyle,
    orderedSections,
    brandRgb,
    cardAnimation,
    getCardAnimationStyle,
    resolvedFamily
  }
}
